import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, SparklesIcon, CreditCardIcon, ArrowRightIcon, ShieldCheckIcon, ClockIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { SUBSCRIPTION_PLANS, type SubscriptionPlan } from '../../../config/subscriptionPlans';
import { SubscriptionTier } from '../../../config/subscriptionTiers';
import { api } from '../../../utils/api';
import { loadStripe } from '@stripe/stripe-js';
import clsx from 'clsx';
import { getDisplayPrice } from '../../../config/pricing';
import { useAuth } from '../../../contexts/AuthContext';
import logoSvg from '../../../assets/images/logo.svg';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || '');

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTier: SubscriptionTier;
  requiredTier?: SubscriptionTier;
  feature?: string;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  currentTier,
  requiredTier,
  feature
}) => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');
  const [loading, setLoading] = useState<SubscriptionTier | null>(null);
  const { user, loading: authLoading } = useAuth();
  
  // Don't render until auth is loaded
  if (authLoading) {
    return null;
  }
  
  const formatPrice = (plan: SubscriptionPlan, period: 'monthly' | 'annual') => {
    const priceInfo = getDisplayPrice(
      user?.country || 'US',
      plan.tier,
      period
    );
    
    const price = `${priceInfo.symbol}${priceInfo.amount}`;
    const term = `${priceInfo.currency}/${period === 'monthly' ? 'month' : 'year'}`;
    
    return [price, term];
  };

  // Get only the paid plans (PRO and BUSINESS_PRO)
  const paidPlans = SUBSCRIPTION_PLANS.filter(plan => plan.tier !== 'FREE');

  // Get the plans that are higher than the current tier
  const upgradePlans = paidPlans.filter(plan => {
    // For PRO users, only show BUSINESS_PRO
    if (currentTier === 'PRO') {
      return plan.tier === 'BUSINESS_PRO';
    }
    
    // For FREE users, show both PRO and BUSINESS_PRO
    return true;
  });

  // If a specific tier is required, highlight that plan
  const highlightedPlan = requiredTier 
    ? paidPlans.find(plan => plan.tier === requiredTier)
    : upgradePlans[0];

  const handleUpgrade = async (plan: SubscriptionPlan) => {
    try {
      setLoading(plan.tier);
      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe not initialized');

      // Create the checkout session with tier and billing period
      const response = await api.post<{ sessionId: string }>('/subscription/create-checkout-session', {
        tier: plan.tier,
        billingPeriod
      });

      // Redirect to checkout
      const { error } = await stripe.redirectToCheckout({ 
        sessionId: response.data.sessionId 
      });
      
      if (error) throw error;
    } catch (err: any) {
      console.error('Upgrade error:', err);
      // Handle error appropriately
    } finally {
      setLoading(null);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel className="mx-auto w-full max-w-3xl rounded-xl bg-white shadow-2xl my-auto relative">
          <div className="max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-start">
              <div className="flex flex-col items-center w-full">
                <img src={logoSvg} alt="SayCard Logo" className="h-12 mb-4" />
                <Dialog.Title className="text-2xl font-bold text-gray-900">
                  Upgrade Your Plan
                </Dialog.Title>
                {feature && (
                  <Dialog.Description className="mt-2 text-gray-600">
                    Upgrade to access {feature}
                  </Dialog.Description>
                )}
                <div className="flex flex-wrap justify-center gap-4 mt-4 px-2">
                  <div className="flex items-center gap-2 text-gray-600">
                    <ClockIcon className="h-5 w-5 text-blue-500" />
                    <span className="text-sm font-medium">Instant access</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <ShieldCheckIcon className="h-5 w-5 text-green-500" />
                    <span className="text-sm font-medium">No Lock-in contracts</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <ArrowPathIcon className="h-5 w-5 text-purple-500" />
                    <span className="text-sm font-medium">Cancel Anytime</span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Upsell Messaging */}
            <div className="mt-8 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Make your first impression count.</h3>
              <p className="text-gray-700 mb-4">Basic plans let you share contact info. Business Pro lets you share what matters.</p>
              
              <div className="grid md:grid-cols-3 gap-4 mt-5">
                <div className="flex flex-col">
                  <div className="flex items-center mb-2">
                    <span className="text-xl mr-2">🔗</span>
                    <h4 className="font-semibold text-gray-800">Links that work</h4>
                  </div>
                  <p className="text-sm text-gray-600">Add resumes, portfolios, and folders directly in your vCard</p>
                </div>
                
                <div className="flex flex-col">
                  <div className="flex items-center mb-2">
                    <span className="text-xl mr-2">🖼️</span>
                    <h4 className="font-semibold text-gray-800">Rich media</h4>
                  </div>
                  <p className="text-sm text-gray-600">Share galleries and videos via Dropbox, Drive, or Flickr</p>
                </div>
                
                <div className="flex flex-col">
                  <div className="flex items-center mb-2">
                    <span className="text-xl mr-2">💼</span>
                    <h4 className="font-semibold text-gray-800">Professional networks</h4>
                  </div>
                  <p className="text-sm text-gray-600">Connect LinkedIn, GitHub, and more with a single tap</p>
                </div>
              </div>
              
              <p className="mt-4 text-sm text-gray-700 italic">Your vCard becomes a smart, interactive snapshot of everything you do — instantly shareable by QR or link.</p>
            </div>

            {/* Billing Period Toggle */}
            <div className="mt-6 flex justify-center sticky top-0 bg-white z-10 py-2">
              <div className="inline-flex rounded-lg bg-gray-100 p-1">
                <button
                  className={clsx(
                    'px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2',
                    billingPeriod === 'monthly'
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  )}
                  onClick={() => setBillingPeriod('monthly')}
                >
                  <CreditCardIcon className="h-4 w-4" />
                  Pay Monthly
                </button>
                <button
                  className={clsx(
                    'px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2',
                    billingPeriod === 'annual'
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  )}
                  onClick={() => setBillingPeriod('annual')}
                >
                  <SparklesIcon className="h-4 w-4" />
                  Pay Yearly & SAVE
                </button>
              </div>
            </div>

            {/* Plans */}
            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              {upgradePlans.map((plan) => (
                <div
                  key={plan.tier}
                  className={clsx(
                    'rounded-lg border p-6',
                    plan === highlightedPlan
                      ? 'border-blue-200 bg-blue-50 shadow-sm'
                      : 'border-gray-200'
                  )}
                >
                  <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                  
                  <div className="mt-4 flex items-baseline text-5xl font-bold">
                    {formatPrice(plan, billingPeriod).map((part, i) => (
                      <span key={i} className={i === 0 ? '' : 'ml-1 text-2xl font-medium text-gray-500'}>
                        {part}
                      </span>
                    ))}
                  </div>
                  {billingPeriod === 'annual' && plan.annualSavings && (
                    <p className="mt-1 text-sm text-green-600 font-semibold">
                      Save! 2 months FREE with annual plan
                    </p>
                  )}
                  <ul className="mt-6 space-y-4">
                    {plan.features.map((feature, index) => {
                      if (feature.feature === 'Enhanced Speech Analysis') {
                        return (
                          <li key={index}>
                            <div className="flex items-start text-blue-600 font-medium">
                              <SparklesIcon className="h-6 w-6 flex-shrink-0 text-blue-500" />
                              <span className="ml-3">{feature.feature}</span>
                            </div>
                            <p className="ml-9 mt-1 text-sm font-bold text-gray-700">
                              {feature.description}
                            </p>
                          </li>
                        );
                      }
                      
                      return (
                        <li
                          key={index}
                          className={clsx(
                            'flex items-start',
                            feature.highlight ? 'text-blue-600 font-medium' : 'text-gray-600'
                          )}
                        >
                          <svg
                            className="h-6 w-6 flex-shrink-0 text-green-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="ml-3">
                            {feature.feature}
                            {feature.description && (
                              <p className="text-sm text-gray-500">{feature.description}</p>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  <button
                    onClick={() => handleUpgrade(plan)}
                    disabled={loading === plan.tier}
                    className={clsx(
                      'mt-8 w-full rounded-lg px-4 py-2 text-sm font-medium text-white flex items-center justify-center gap-2',
                      loading === plan.tier && 'opacity-75 cursor-not-allowed',
                      plan === highlightedPlan
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-gray-600 hover:bg-gray-700'
                    )}
                  >
                    {loading === plan.tier ? (
                      <>
                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCardIcon className="h-4 w-4" />
                        Upgrade Now
                        <ArrowRightIcon className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
